import type {
  AutomationFlowStepToRequest,
  AutomationFlowSettings,
  AutomationFlowInterface,
  AutomationFlowStepFromResponse,
} from '@/data/models/domain/automations/types'
import type { JSONContent } from '@tiptap/core'

export enum AutomationAiBuilderUnavailableReason {
  DAILY_LIMIT_REACHED = 'daily_limit_reached',
}

export interface AutomationAiBuilderAvailabilityQuota {
  limit: number
  used: number
  remaining: number
}

export interface AutomationAiBuilderAvailabilityResponse {
  available: boolean
  reason: AutomationAiBuilderUnavailableReason | null
  message: string | null
  resetAt: string // ISO timestamp
  quota: AutomationAiBuilderAvailabilityQuota
}

export enum AutomationAiBuilderActivityTool {
  ADD_NODE = 'add_node',
  CONNECT = 'connect',
  GET_APP_LINK = 'get_app_link',
  GET_GRAPH = 'get_graph',
  GET_STEP_VARIABLES = 'get_step_variables',
  REMOVE_NODE = 'remove_node',
  SAVE_AUTOMATION = 'save_automation',
  SEARCH_AUTOMATIONS = 'search_automations',
  SEARCH_CUSTOM_FIELD_OPTIONS = 'search_custom_field_options',
  SEARCH_CUSTOM_FIELDS = 'search_custom_fields',
  SEARCH_DEAL_PIPELINES = 'search_deal_pipelines',
  SEARCH_EMAIL_SENDERS = 'search_email_senders',
  SEARCH_FACEBOOK_PAGES = 'search_facebook_pages',
  SEARCH_GROUPS = 'search_groups',
  SEARCH_INSTAGRAM_ACCOUNTS = 'search_instagram_accounts',
  SEARCH_NODES = 'search_nodes',
  SEARCH_SEGMENTS = 'search_segments',
  SEARCH_SHOPIFY_COLLECTIONS = 'search_shopify_collections',
  SEARCH_SHOPIFY_PRODUCTS = 'search_shopify_products',
  SEARCH_SHOPIFY_STORES = 'search_shopify_stores',
  SEARCH_TAGS = 'search_tags',
  SEARCH_TASK_BOARDS = 'search_task_boards',
  SEARCH_TICKET_STATUSES = 'search_ticket_statuses',
  SEARCH_WHATSAPP_PHONES = 'search_whatsapp_phones',
  SEARCH_WOOCOMMERCE_COLLECTIONS = 'search_woocommerce_collections',
  SEARCH_WOOCOMMERCE_CUSTOMER_CUSTOM_FIELDS = 'search_woocommerce_customer_custom_fields',
  SEARCH_WOOCOMMERCE_ORDER_CUSTOM_FIELDS = 'search_woocommerce_order_custom_fields',
  SEARCH_WOOCOMMERCE_PRODUCTS = 'search_woocommerce_products',
  SEARCH_WOOCOMMERCE_STORES = 'search_woocommerce_stores',
  SET_AUTOMATION_SETTINGS = 'set_automation_settings',
  SUGGEST_REPLIES = 'suggest_replies',
  UPDATE_NODE = 'update_node',
}

export enum AutomationAiBuilderMessageRole {
  ASSISTANT = 'assistant',
  USER = 'user',
}

export enum AutomationAiBuilderMessageType {
  ACTIVITY = 'activity',
  TEXT = 'text',
}

export enum AutomationAiBuilderConversationStatus {
  IDLE = 'idle',
  PENDING = 'pending',
  STOPPED = 'stopped',
}

export interface AutomationAiBuilderPromptSuggestion {
  surface: AutomationAiBuilderSurface // Added by FE
  title: string
  prompt: string
}

export type AutomationAiBuilderSimpleSuggestion = string

export type AutomationAiBuilderSuggestion = AutomationAiBuilderPromptSuggestion | AutomationAiBuilderSimpleSuggestion

export interface AutomationAiBuilderPromptSuggestionRequest {
  surface: AutomationAiBuilderSurface
}

export interface AutomationAiBuilderPromptSuggestionResponse {
  suggestions: Pick<AutomationAiBuilderPromptSuggestion, 'title' | 'prompt'>[]
}

export enum AutomationAiBuilderSurface {
  AUTOMATION_FLOW = 'automation_flow',
  AUTOMATION_MANAGE = 'automation_manage',
}

type AutomationAiBuilderBaseMessageResponse = {
  surface: AutomationAiBuilderSurface
  position: number // May be the same for multiple messages in the same conversation. Do not use this to sort messages.
  createdAt: string
}

type AutomationAiBuilderBaseMessage = Pick<AutomationAiBuilderBaseMessageResponse, 'surface'> & {
  id: string // Added by FE
  conversationId: number // Added by FE
  isPending?: boolean // Added by FE, it's true if not all chunks are received yet
  order: number // Added by FE, may be decimal
}

export type AutomationAiBuilderTextMessageResponse = AutomationAiBuilderBaseMessageResponse & {
  type: AutomationAiBuilderMessageType.TEXT
  role: AutomationAiBuilderMessageRole
  text: string
}

export type AutomationAiBuilderTextMessage = Omit<AutomationAiBuilderTextMessageResponse, 'position'> &
  AutomationAiBuilderBaseMessage

export type AutomationAiBuilderActivityMessageResponse = AutomationAiBuilderBaseMessageResponse & {
  type: AutomationAiBuilderMessageType.ACTIVITY
  role: AutomationAiBuilderMessageRole.ASSISTANT
  tool: AutomationAiBuilderActivityTool
  label: string
}

export type AutomationAiBuilderActivityMessage = Omit<AutomationAiBuilderActivityMessageResponse, 'position'> &
  AutomationAiBuilderBaseMessage

export type AutomationAiBuilderMessageResponse =
  | AutomationAiBuilderTextMessageResponse
  | AutomationAiBuilderActivityMessageResponse

export type AutomationAiBuilderMessage = AutomationAiBuilderTextMessage | AutomationAiBuilderActivityMessage

export type AutomationAiBuilderMessageToAddToTheEnd =
  | Omit<AutomationAiBuilderTextMessage, 'id' | 'conversationId' | 'order'>
  | Omit<AutomationAiBuilderActivityMessage, 'id' | 'conversationId' | 'order'>

export interface AutomationAiBuilderConversationResponse {
  conversationId: number | null
  messages: AutomationAiBuilderMessageResponse[]
  pendingSuggestions: AutomationAiBuilderSimpleSuggestion[]
}

export interface AutomationAiBuilderSendMessageInDraftFlowRequest {
  message: string
  flow: {
    name: string
    steps: AutomationFlowStepToRequest[]
    settings: AutomationFlowSettings
  }
}

export interface AutomationAiBuilderSendMessageInActiveFlowRequest {
  message: string
  surface: AutomationAiBuilderSurface.AUTOMATION_MANAGE
}

export type AutomationAiBuilderSendMessageRequest =
  | AutomationAiBuilderSendMessageInDraftFlowRequest
  | AutomationAiBuilderSendMessageInActiveFlowRequest

export interface AutomationAiBuilderFetchMessagesRequest {
  perPage: number
  beforePosition?: number
}

export interface AutomationAiBuilderFetchMessagesResponse {
  items: AutomationAiBuilderMessageResponse[]
  hasMore: boolean
  nextCursor: number
}

// >>> WebSocket event payloads

export interface AutomationAiBuilderEventChunkPayload {
  conversationId: number
  chunk: string
}

export interface AutomationAiBuilderEventStatusPayload {
  conversationId: number
  tool: AutomationAiBuilderActivityTool
  label: string
}

export interface AutomationAiBuilderEventSuggestionsPayload {
  conversationId: number
  options: AutomationAiBuilderSimpleSuggestion[]
}

export interface AutomationAiBuilderEventDonePayload {
  conversationId: number
  automationId: number | null
}

export interface AutomationAiBuilderEventAvailabilityPayload {
  conversationId: number
  availability: AutomationAiBuilderAvailabilityResponse
}

export interface AutomationAiBuilderEventStoppedPayload {
  conversationId: number
}

export interface AutomationAiBuilderEventFlowPayload {
  conversationId: number
  flow: {
    name: string
    steps: AutomationFlowStepToRequest[]
    settings: AutomationFlowSettings
  }
}

export interface AutomationAiBuilderEventAutomationPayload {
  conversationId: number
  automation: AutomationFlowInterface
}

export interface AutomationAiBuilderEventStepsPayload {
  conversationId: number
  automationId: number
  steps: AutomationFlowStepFromResponse[] // contains only the steps that were updated by the AI
}

// <<< WebSocket event payloads

export interface AutomationFlowAiBuilderResolverParams {
  automationId: string
}

export type AutomationFlowAiBuilderMessageListState = {
  showOlderMessages: boolean
  isAllMessagesLoaded: boolean
  isNextPageLoading: boolean
  nextPageCursor: number | undefined // undefined means that the first page have not yet been loaded
}

export type AutomationFlowAiBuilderStateType = {
  promptDraft: JSONContent | null
  messageListState: AutomationFlowAiBuilderMessageListState
}

export const automationFlowAiBuilderSubscriptionStepUpdatedEventName = 'automationFlowAiBuilderSubscriptionStepUpdated'

export interface AutomationFlowAiBuilderSubscriptionStepUpdatedEventPayload {
  automationId: string
  stepLocalIds: number[]
}

export const automationFlowAiBuilderSubscriptionAnyAiEventName = 'automationFlowAiBuilderSubscriptionAnyAiEvent'

export interface AutomationFlowAiBuilderSubscriptionAnyAiEventPayload {
  automationId: string
}
